import type {SetFieldsOperation} from '@github-ui/optimistic-connection'
import {useEntityMutation} from '@github-ui/optimistic-connection/useEntityMutation'
import {unarchiveProjectItemMutation as queryId} from '@github-ui/server-persisted-queries'
import type {UnarchiveProjectItemMutationMutation} from '@github-ui/server-persisted-queries/types/unarchiveProjectItemMutation'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {useCallback} from 'react'

async function unarchiveProjectItemRequest({
  owner,
  repo,
  issueNumber,
  itemId,
}: {
  owner: string
  repo: string
  issueNumber: number
  itemId: string
}): Promise<UnarchiveProjectItemMutationMutation> {
  const query = new URLSearchParams({queryId})
  const response = await reactFetchJSON(
    `/${encodeURIComponent(owner)}/${encodeURIComponent(repo)}/issues/${issueNumber}/project_item/unarchive.json?${query}`,
    {method: 'PATCH', body: {itemId}},
  )

  let result:
    | {data?: UnarchiveProjectItemMutationMutation | null; errors?: ReadonlyArray<{message?: string}> | null}
    | undefined
  try {
    result = (await response.json()) as typeof result
  } catch {
    result = undefined
  }

  const hasErrors = Boolean(result?.errors?.length)
  const error = result?.errors?.find(item => item.message)?.message
  const data = result?.data
  const item = data?.unarchiveProjectV2Item?.item
  if (!response.ok || hasErrors || !data || item?.id !== itemId || item.isArchived !== false) {
    throw new Error(error ?? 'Failed to restore project item')
  }
  return data
}

export function useUnarchiveProjectItemMutation({
  owner,
  repo,
  issueNumber,
  projectItemId,
  onError,
  onCompleted,
}: {
  owner: string
  repo: string
  issueNumber: number
  projectItemId: string
  onError?: (error: Error) => void
  onCompleted?: (response: UnarchiveProjectItemMutationMutation) => void
}) {
  const {mutate} = useEntityMutation<UnarchiveProjectItemMutationMutation>({
    mutationFn: operation => unarchiveProjectItemRequest({owner, repo, issueNumber, itemId: operation.id}),
    getConfirmedOperation: data => {
      const item = data.unarchiveProjectV2Item?.item
      return item ? {type: 'setFields', id: item.id, fields: {isArchived: item.isArchived}} : undefined
    },
    onError,
    onSuccess: onCompleted,
  })

  return useCallback(
    () =>
      mutate({
        operation: {type: 'setFields', id: projectItemId, fields: {isArchived: false}} satisfies SetFieldsOperation,
      }),
    [mutate, projectItemId],
  )
}
